import React, { useEffect, useState, useRef } from "react";
import { CART_API_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

export default function Cart() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState(null);

  const userMobile = localStorage.getItem("userMobileNumber") || "";
  const itemsRef = useRef([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  useEffect(() => {
    if (!userMobile) {
      setError("Please login to view your cart");
      setLoading(false);
      return;
    }

    const fetchCart = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await authedFetch(
          `${CART_API_URL}/Cart/GetCartProducts?mobileNo=${userMobile}`
        );
        if (!res.ok) throw new Error("Failed to load cart");
        const data = await res.json();
        const products = data?.products || data || [];
        const list = Array.isArray(products) ? products : [];
        setUserId(typeof data?.userId !== "undefined" ? data.userId : userMobile);
        setItems(list);
      } catch (err) {
        setError(err.message || "Unable to load cart");
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [userMobile]);

  const changeQty = async (productId, delta) => {
    if (!userMobile || !productId) return;
    // Stale-closure bug (agent finding): rapid +/- clicks ek hi purane `cur` se
    // increment hote the — latest list ref se lo.
    const item = itemsRef.current.find((p) => (p?.id ?? p?.Id) === productId);
    if (!item) return;
    const cur = Number(item?.quantity ?? item?.Quantity ?? 1) || 1;
    const next = Math.max(1, cur + delta);
    if (next === cur) return;
    setItems((prev) =>
      prev.map((p) => (p?.id ?? p?.Id) === productId ? { ...p, Quantity: next } : p)
    );
    try {
      const res = await authedFetch(`${CART_API_URL}/Cart/UpdateQuantity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: userMobile,
          productId,
          quantity: next,
        }),
      });
      if (!res.ok) throw new Error("Qty update failed");
    } catch (err) {
      setError(err.message || "Unable to update quantity");
    }
  };

  const removeItem = async (productId) => {
    if (!userMobile || !productId) return;
    try {
      const res = await authedFetch(`${CART_API_URL}/Cart/UpdateCart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: userMobile,
          userId,
          productId,
          action: "removeProduct",
        }),
      });
      if (!res.ok) throw new Error("Remove failed");
      setItems((prev) => prev.filter((p) => (p?.id ?? p?.Id) !== productId));
    } catch (err) {
      setError(err.message || "Unable to remove item");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", fontWeight: 600 }}>
        Loading cart...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <p style={{ color: "#dc3545" }}>{error}</p>
        {!userMobile && (
          <button onClick={() => navigate("/login")} style={{ padding: "10px 24px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
            Login
          </button>
        )}
      </div>
    );
  }

  const total = items.reduce(
    (sum, p) => sum + (Number(p?.price ?? p?.Price) || 0) * (Number(p?.quantity ?? p?.Quantity) || 1),
    0
  );

  if (items.length === 0) {
    return (
      <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ margin: "0 0 16px", fontSize: "24px", fontWeight: 800, color: "#0f172a" }}>
          Your Cart
        </h2>
        <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
          Your cart is empty
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", minHeight: "70vh" }}>
      <Seo title="Your Cart & Checkout" description="Aapka AIO India cart — selected products aur total price dekhkar aasani se checkout karein." path="/cart" />
      <h2 style={{ margin: "0 0 16px", fontSize: "24px", fontWeight: 800, color: "#0f172a" }}>
        Your Cart ({items.length})
      </h2>

      <div
        style={{
          display: "grid",
          gap: "16px",
        }}
      >
        {items.map((item) => {
          const id = item?.id ?? item?.Id;
          const name = item?.name ?? item?.ProductName ?? "Product";
          const price = item?.price ?? item?.Price;
          const image = item?.image ?? item?.ProductImage;

          return (
            <div
              key={id}
              style={{
                display: "flex",
                gap: "16px",
                background: "#fff",
                borderRadius: "12px",
                padding: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                alignItems: "center",
                border: "1px solid #f1f5f9",
              }}
            >
              <div style={{ width: "80px", height: "80px", borderRadius: "8px", overflow: "hidden", background: "#cbd5e1", flexShrink: 0 }}>
                {image && (
                  <img
                    src={image}
                    alt={name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                )}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "15px" }}>{name}</div>
                <div style={{ fontSize: "14px", color: "#16a34a", fontWeight: 700, marginTop: "4px" }}>
                  {price !== undefined && price !== null ? `₹${price}` : ""}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                  <button
                    type="button"
                    onClick={() => changeQty(id, -1)}
                    style={{
                      width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #ddd",
                      background: "#f8fafc", color: "#0f172a", fontWeight: 800, cursor: "pointer",
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a", minWidth: "24px", textAlign: "center" }}>
                    {item?.quantity ?? item?.Quantity ?? 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => changeQty(id, 1)}
                    style={{
                      width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #ddd",
                      background: "#f8fafc", color: "#0f172a", fontWeight: 800, cursor: "pointer",
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeItem(id)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                  background: "#fff",
                  color: "#dc2626",
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #f1f5f9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: "18px", color: "#0f172a" }}>Total: ₹{total}</div>
        <button
          type="button"
          onClick={() => navigate("/checkout", { state: { items, total } })}
          style={{
            padding: "12px 24px",
            borderRadius: "10px",
            border: "none",
            background: "linear-gradient(135deg, #ff9f00, #fb641b)",
            color: "#fff",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 6px 16px rgba(251,100,27,0.35)",
          }}
        >
          Checkout
        </button>
      </div>
    </div>
  );
}
